/**
 * ProposalUrgencyNote — Tier 6E.1 proposal urgency layer
 *
 * Encourages timely decision-making **without** harming brand trust:
 * no countdown timers, no alarm colors, no scarcity theatrics.
 *
 * Three soft cues, stacked with intentional whitespace:
 *   1. "This proposal is prepared specifically for your event and is
 *       subject to availability."  (personalisation + scarcity, gentle)
 *   2. "We recommend securing your date soon to ensure availability."
 *      (suggested action, premium voice)
 *   3. "Proposal valid for a limited time." (static, no expiration date)
 *
 * Design language:
 *   - Subtle Clock icon in gold (no red)
 *   - White/55 body copy, italic 3rd line at white/35
 *   - Single white-glass card on the existing luxury palette
 *   - Mobile: copy stacks naturally below the icon at 13px
 *
 * Props:
 *   - showLimitedTime?: boolean (default true)
 *     The optional 3rd line. Pass `false` to hide if the tenant prefers
 *     just the first two cues.
 */
import React from 'react';
import { Clock } from 'lucide-react';

const ProposalUrgencyNote = ({ showLimitedTime = true }) => {
  return (
    <div
      className="rounded-2xl px-5 py-4 sm:px-6 sm:py-5 flex items-start gap-3.5 sm:gap-4"
      style={{
        background:
          'linear-gradient(180deg, rgba(212,175,55,0.05), rgba(212,175,55,0.015))',
        border: '1px solid rgba(212,175,55,0.18)',
      }}
      data-testid="proposal-urgency-note"
      role="note"
      aria-label="Proposal availability note"
    >
      <div
        className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: 'rgba(212,175,55,0.10)',
          border: '1px solid rgba(212,175,55,0.22)',
        }}
        aria-hidden
      >
        <Clock
          className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
          style={{ color: '#D4AF37' }}
          strokeWidth={2}
        />
      </div>

      <div className="flex-1 min-w-0">
        <p
          className="text-[13px] sm:text-[13.5px] text-white/72 leading-relaxed"
          data-testid="proposal-urgency-availability"
        >
          This proposal is prepared for your event and is subject to
          availability.
        </p>
        <p
          className="mt-1.5 text-[13px] sm:text-[13.5px] text-white/55 leading-relaxed"
          data-testid="proposal-urgency-recommendation"
        >
          We recommend securing your date soon.
        </p>
        {showLimitedTime && (
          <p
            className="mt-2 text-[11.5px] text-white/35 italic leading-relaxed"
            data-testid="proposal-urgency-limited-time"
          >
            Proposal valid for a limited time.
          </p>
        )}
      </div>
    </div>
  );
};

export default ProposalUrgencyNote;
export { ProposalUrgencyNote };
